import { inject, InjectionKey, provide } from "vue";
import { SidUserClient } from "@sid/sid_client";
import { QuestionnaireRouteNames } from "@/commons/questionnaire/questionnaire";
import { reEncodeUriComponent } from "@/commons/commonUtility";

/** QuestionnaireItemインジェクションキー */
const QuestionnaireItemKey: InjectionKey<QuestionnaireItem> = Symbol("QuestionnaireItem");

/** QuestionnaireInfoインタフェース */
export interface QuestionnaireInfo {
    codeChallenge: string;
    callbackUrl: string;
    siteCode: string;
    mmFlg: string | null;
    mmExtId: string | null;
    callbackUrlWithToken: string | null;
}

/** 連携情報 */
export class QuestionnaireItem {

    /** 連携情報 */
    public readonly questionnaireInfo: QuestionnaireInfo = {
        codeChallenge: "",
        callbackUrl: "",
        siteCode: "",
        mmFlg: null,
        mmExtId: null,
        callbackUrlWithToken: null,
    };

    /** ルート情報 */
    public readonly routeNames = QuestionnaireRouteNames;

    /**
     * インスタンス生成
     * @param linkageName
     * @param defaultUrl
     */
    public static create() {
        return new QuestionnaireItem();
    }

    /**
     * インジェクション
     */
    public static inject(): QuestionnaireItem {
        const info = inject(QuestionnaireItemKey);
        if (info) {
            return info;
        }
        throw new Error("not provided.");
    }

    /**
     * コンストラクタ
     */
    public constructor() {
        this.loadStorage();
    }

    /**
     * 連携情報設定
     * @param info
     */
    public setLinkageInfo(info: QuestionnaireInfo) {
        this.questionnaireInfo.codeChallenge = info.codeChallenge;
        this.questionnaireInfo.callbackUrl = info.callbackUrl;
        this.questionnaireInfo.siteCode = info.siteCode;
        this.questionnaireInfo.mmFlg = info.mmFlg;
        this.questionnaireInfo.mmExtId = info.mmExtId;
        this.questionnaireInfo.callbackUrlWithToken = info.callbackUrlWithToken;
    }

    /**
     * ユーザトークン取得
     * @param sidClient
     * @param force
     */
    public fetchToken = async (sidClient: SidUserClient, force = false) => {
        if (this.questionnaireInfo.callbackUrlWithToken && !force) {
            return;
        }
        const response = await sidClient.requestApi({
            method: "GET",
            path: "/linkage/token",
            queries: {
                "link_id": "-1",
                "code_challenge": this.questionnaireInfo.codeChallenge,
                "callback": reEncodeUriComponent(this.questionnaireInfo.callbackUrl)
            }
        });
        this.questionnaireInfo.callbackUrlWithToken = response.data.callback;
    }

    /**
     * このインスタンスをprovide()する
     */
    public provide() {
        provide(QuestionnaireItemKey, this);
    }

    /**
     * セッションストレージから読み出す
     */
    public loadStorage() {
        if (sessionStorage[this.storageKey])
            this.setLinkageInfo(JSON.parse(sessionStorage[this.storageKey]));
    }

    /**
     * セッションストレージに格納
     */
    public storeStorage() {
        sessionStorage[this.storageKey] = JSON.stringify(this.questionnaireInfo);
    }

    /**
     * セッションストレージから削除
     */
    public removeStorage() {
        sessionStorage.removeItem(this.storageKey);
    }

    /**
     * セッションストレージへの格納キー
     */
    get storageKey() {
        return "QuestionnaireInfo";
    }
}
